import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

import {
  NgWhiteboardModule,
  NgWhiteboardService,
  ToolsEnum,
  WhiteboardElement,
} from '../../shared/components/ng-whiteboard';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faWrench } from '@fortawesome/pro-duotone-svg-icons/faWrench';
import { faDownload } from '@fortawesome/pro-duotone-svg-icons/faDownload';
import { faTimeline } from '@fortawesome/pro-duotone-svg-icons/faTimeline';
import { faBroomWide } from '@fortawesome/pro-duotone-svg-icons/faBroomWide';
import { faShapes } from '@fortawesome/pro-duotone-svg-icons/faShapes';
import { faFileSvg } from '@fortawesome/pro-duotone-svg-icons/faFileSvg';
import { faFilePng } from '@fortawesome/pro-duotone-svg-icons/faFilePng';
import { faPenSwirl } from '@fortawesome/pro-duotone-svg-icons/faPenSwirl';
import { faDrawSquare } from '@fortawesome/pro-duotone-svg-icons/faDrawSquare';
import { faDrawCircle } from '@fortawesome/pro-duotone-svg-icons/faDrawCircle';
import { faRuler } from '@fortawesome/pro-duotone-svg-icons/faRuler';
import { faDumpsterFire } from '@fortawesome/pro-duotone-svg-icons/faDumpsterFire';
import { faTrash } from '@fortawesome/pro-duotone-svg-icons/faTrash';
import { faEraser } from '@fortawesome/pro-duotone-svg-icons/faEraser';
import { faBackward } from '@fortawesome/pro-duotone-svg-icons/faBackward';
import { faForward } from '@fortawesome/pro-duotone-svg-icons/faForward';
import { faEyeSlash } from '@fortawesome/pro-duotone-svg-icons/faEyeSlash';
import { faSwatchbook } from '@fortawesome/pro-duotone-svg-icons/faSwatchbook';
import { OdinService } from '../../core/services/odin.service';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { TooltipDirective } from '../../shared/components/ui/tooltip/tooltip.directive';
import { UserAvatar } from '../../core/components/user-avatar/user-avatar';
import { UserStore } from '../../state/user.repository';
import { UserService } from '../../core/services/user.service';
import { PermissionStore } from '../../state/permissions.repository';
import { TranslatePipe } from '@ngx-translate/core';

enum AvailableColors {
  AUTO = 'rgb(var(--text-color))',
  BLUE = '#293462',
  CYAN = '#1cd6ce',
  YELLOW = '#fedb39',
  RED = '#d61c4e',
}

@Component({
  selector: 'andor-whiteboard',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    NgWhiteboardModule,
    FontAwesomeModule,
    TooltipDirective,
    UserAvatar,
  ],
  templateUrl: './whiteboard.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './whiteboard.scss',
})
export class Whiteboard implements OnInit {
  protected readonly faWrench = faWrench;
  protected readonly faDownload = faDownload;
  protected readonly faTimeline = faTimeline;
  protected readonly faBroomWide = faBroomWide;
  protected readonly faShapes = faShapes;
  protected readonly faFileSvg = faFileSvg;
  protected readonly faFilePng = faFilePng;
  protected readonly faPenSwirl = faPenSwirl;
  protected readonly faDrawSquare = faDrawSquare;
  protected readonly faDrawCircle = faDrawCircle;
  protected readonly faRuler = faRuler;
  protected readonly faDumpsterFire = faDumpsterFire;
  protected readonly faTrash = faTrash;
  protected readonly faEraser = faEraser;
  protected readonly faBackward = faBackward;
  protected readonly faForward = faForward;
  protected readonly faEyeSlash = faEyeSlash;
  protected readonly faSwatchbook = faSwatchbook;

  protected readonly tools = ToolsEnum;
  protected readonly colors = AvailableColors;

  constructor(
    public whiteboardService: NgWhiteboardService,
    public odinService: OdinService,
    public userSettingsStore: UserSettingsStore,
    public userStore: UserStore,
    public userService: UserService,
    private permissionStore: PermissionStore
  ) {}

  protected tool: ToolsEnum = ToolsEnum.BRUSH;
  protected color: AvailableColors = AvailableColors.AUTO;

  ngOnInit(): void {}

  async exportWhiteboard(type: 'svg' | 'png') {
    const now = new Date();
    let fileName = `${now.getFullYear()}.${now.getMonth()}.${now.getDate()}_${now.getHours()}.${now.getMinutes()}_`;
    fileName += this.userSettingsStore.value.roomName;
    fileName += '_whiteboard.';
    fileName += type;

    this.whiteboardService.save(type, fileName);
  }

  selectTool(tool: ToolsEnum) {
    this.tool = tool;
  }

  selectColor(color: AvailableColors) {
    this.color = color;
  }

  async onChange(data: WhiteboardElement[]) {
    await this.odinService.sendMessage(
      {
        id: crypto.randomUUID(),
        scope: 'room',
        payload: data,
        kind: 'whiteboard',
      },
      []
    );
  }

  parseInt(str: string) {
    return parseInt(str);
  }

  toggleVisibility(layer: string) {
    const foreignData = this.whiteboardService.foreignData.value;
    if (!foreignData[layer]) return;
    foreignData[layer].visible = !foreignData[layer].visible;
    this.whiteboardService.foreignData.next(foreignData);
  }

  async clearPage() {
    if (!this.permissionStore.value.clearDrawings) return;
    await this.odinService.sendMessage(
      {
        id: crypto.randomUUID(),
        scope: 'room',
        kind: 'whiteboard-clear',
        payload: {},
      },
      []
    );
  }
}
